import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, Eye, EyeOff, Loader2, Leaf, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export function AuthModal({ onClose }) {
  const [tab, setTab] = useState('register');
  const [form, setForm] = useState({ full_name: '', email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSuccess = () => {
    sessionStorage.setItem('from_quiz', 'true');
    navigate('/my-plan');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error('Паролата трябва да е поне 6 символа'); return; }
    setAuthLoading(true);
    try {
      await register(form.email, form.password, form.full_name, null);
      handleSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Грешка при регистрацията');
    } finally { setAuthLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      await login(form.email, form.password);
      handleSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Грешни имейл или парола');
    } finally { setAuthLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        className="relative z-10 w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden"
        initial={{ opacity: 0, y: 64, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.97 }}
        transition={{ type: 'spring', damping: 26, stiffness: 300 }}
        data-testid="auth-modal"
      >
        {/* Header */}
        <div
          className="relative p-6 pb-5 text-center"
          style={{ background: 'linear-gradient(135deg, #10b981 0%, #065f46 100%)' }}
        >
          <button
            onClick={onClose}
            data-testid="auth-modal-close"
            className="absolute top-4 right-4 text-emerald-200 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Leaf className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-lg font-bold text-white">Само още 1 стъпка</h2>
          <p className="text-emerald-200 text-sm mt-1">Запази резултатите и виж пълния план</p>
          {/* Teaser strip */}
          <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-white/10">
            {['7-дневен план', 'Рецепти', 'Списък'].map(t => (
              <div key={t} className="flex items-center gap-1 text-emerald-200 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" />
                {t}
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 dark:border-slate-700">
          {[['register', 'Нова регистрация'], ['login', 'Вход']].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`auth-tab-${id}`}
              className={`flex-1 py-3 text-sm font-semibold transition-all duration-200 border-b-2 ${
                tab === id
                  ? 'text-emerald-600 dark:text-emerald-400 border-emerald-500'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Form */}
        <div className="p-5">
          <AnimatePresence mode="wait">
            <motion.form
              key={tab}
              initial={{ opacity: 0, x: tab === 'register' ? -16 : 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: tab === 'register' ? 16 : -16 }}
              transition={{ duration: 0.18 }}
              onSubmit={tab === 'register' ? handleRegister : handleLogin}
              className="space-y-3.5"
            >
              {tab === 'register' && (
                <div>
                  <Label className="text-sm font-medium text-gray-900 dark:text-gray-200">Твоето име</Label>
                  <Input
                    placeholder="Иван Петров"
                    value={form.full_name}
                    onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                    className="mt-1.5 h-10 dark:bg-slate-800 dark:border-slate-600"
                    required
                    data-testid="auth-modal-name"
                  />
                </div>
              )}
              <div>
                <Label className="text-sm font-medium text-gray-900 dark:text-gray-200">Имейл</Label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="mt-1.5 h-10 dark:bg-slate-800 dark:border-slate-600"
                  required
                  data-testid="auth-modal-email"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-900 dark:text-gray-200">Парола</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    placeholder="мин. 6 символа"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    className="h-10 pr-9 dark:bg-slate-800 dark:border-slate-600"
                    required
                    data-testid="auth-modal-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={authLoading}
                data-testid="auth-modal-submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-500 text-white h-11 font-semibold rounded-xl mt-1 shadow-lg shadow-emerald-500/25"
              >
                {authLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {tab === 'register' ? 'Създай акаунт и виж плана' : 'Влез и виж плана'}
                    <ChevronRight className="w-4 h-4 ml-1.5" />
                  </>
                )}
              </Button>
            </motion.form>
          </AnimatePresence>

          <p className="text-center text-xs text-slate-400 dark:text-slate-500 mt-3">
            Безплатна регистрация. Без кредитна карта.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
